/** /line-sets/:id/:tab? — line-set editor shell. Tabs: Routes (the nested
 *  editor) + Used by (the tours / itinerary groups referencing this set).
 *  Deleting a set is not blocked — linked tours / itinerary groups are
 *  set-null, not protected. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as routeSetsApi from '@/api/routeSets';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { lineSetsHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { UsedByPanel } from '@/components/UsedByPanel/UsedByPanel';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';

import { LineSetRoutesTab } from './tabs/LineSetRoutesTab';

const TABS = [
  { id: 'routes', label: 'Routes' },
  { id: 'used-by', label: 'Used by' },
] as const;

export function LineSetDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: set,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['route_sets', 'detail', id],
    queryFn: () => routeSetsApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => void refetch();

  const deleteMutation = useMutation({
    mutationFn: () => routeSetsApi.remove(id),
    onSuccess: () => {
      snackbar.show('Line set deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['route_sets'] });
      navigate('/line-sets');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const canViewHistory = hasPerm('console_audit_view');
  const tabs = useMemo(
    () => (canViewHistory ? [...TABS, { id: 'history', label: 'History' } as const] : [...TABS]),
    [canViewHistory],
  );
  const activeTab = useMemo(() => tabs.find((t) => t.id === tab)?.id ?? 'routes', [tabs, tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid line-set id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !set) return <Alert severity="error">Couldn’t load line set.</Alert>;

  return (
    <Box>
      <PageHeader
        helpStory="bus-stops-line-sets"
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={set.name}
        subtitle={
          <IdentitySubtitle
            tenant={set.tenant}
            grid={{ href: lineSetsHref({ tenant: set.tenant.id }), label: 'Line sets of this operator' }}
            id={set.id}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              variant="outlined"
              label={`${set.route_count} route${set.route_count === 1 ? '' : 's'}`}
            />
            <PermissionGate perm="console_fleet_manage">
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/line-sets/${id}/${v}`)}>
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'routes'}>
        <LineSetRoutesTab set={set} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <UsedByPanel resource="route_sets" id={set.id} />
      </TabPane>
      <TabPane show={activeTab === 'history'}>
        <HistoryPanel contentType="demo.vehicle_route_set" objectPk={String(set.id)} onRestored={onSaved} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        Last updated {formatRelativeTime(set.updated_ts)} ·{' '}
        <RouterLink to={lineSetsHref({ tenant: set.tenant.id })} style={{ color: '#0a1a3f' }}>
          ← Back to line sets
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this line set?"
        message={`"${set.name}" will be deleted. Tours and itinerary groups referencing it will have the reference cleared. This cannot be undone.`}
        confirmLabel="Delete line set"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
